import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { useHistory } from 'react-router-dom'
import { Spinner } from 'reactstrap'
import GoogleLogin from 'react-google-login'

import Password from '_components/Password'

import './UserLoginForm.scss'

const UserLoginForm = ({
  onClose,
  onSubmitLogin,
  onGoogleSuccess,
  isSubmitting,
  initialEmail,
}) => {
  const history = useHistory()
  const [email, setEmail] = useState(initialEmail || '')
  const [pin, setPin] = useState(new Array(4))
  const [emailError, setEmailError] = useState('')
  const [pinError, setPinError] = useState('')

  const onPinChanged = (pinEntry, index) => {
    const newPin = [...pin]
    newPin[index] = pinEntry
    setPin(newPin)
  }

  useEffect(() => {
    setEmail(initialEmail || '')
  }, [initialEmail])

  const validateEmail = () => {
    const trimmedEmail = email.trim()
    if (!trimmedEmail) {
      setEmailError('Please enter your email')
      return false
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (!emailRegex.test(trimmedEmail)) {
      setEmailError('Please enter a valid email')
      return false
    }

    setEmailError('')
    return true
  }

  const validatePIN = () => {
    if (pin.some((item) => item === undefined) || pin[0] === undefined) {
      setPinError('Please enter your PIN')
      return false
    }

    setPinError('')
    return true
  }

  const handleSubmit = () => {
    const isEmailValid = validateEmail()
    const isPinValid = validatePIN()

    if (!isEmailValid || !isPinValid) return

    if (onSubmitLogin) {
      onSubmitLogin({
        email: email.trim(),
        pin,
      })
    }
  }

  return (
    <div className='user-login-form'>
      <div className='user-login-form__header'>
        <h3 className='user-login-form__title'>Log In</h3>
        <button
          type='button'
          className='user-login-form__close'
          onClick={onClose}
          aria-label='Close'
        >
          ✕
        </button>
      </div>

      <div className='user-login-form__body'>
        <div className='user-login-form__field'>
          <label className='user-login-form__label' htmlFor='user-login-email'>
            Email
          </label>
          <input
            id='user-login-email'
            type='email'
            className='user-login-form__input'
            placeholder='Enter your email'
            value={email}
            onChange={(event) => {
              setEmail(event.target.value)
              if (emailError) {
                setEmailError('')
              }
            }}
          />
          {emailError && <small className='user-login-form__error'>{emailError}</small>}
        </div>

        <div className='user-login-form__pin-section'>
          <div className='user-login-form__label'>PIN</div>
          <Password
            isFirstPin={true}
            pinLength={4}
            onPinChanged={onPinChanged}
            pin={pin}
            isMasked={true}
          />
          {pinError && <small className='user-login-form__error'>{pinError}</small>}
        </div>

        <div className='user-login-form__actions'>
          <button
            type='button'
            className='user-login-form__submit-btn'
            onClick={handleSubmit}
            disabled={isSubmitting}
          >
            {isSubmitting ? <Spinner color='light' size='sm' /> : 'Log In'}
          </button>
        </div>

        <p className='user-login-form__terms'>
          By logging in to Co-bee, I agree Co-bee&apos;s{' '}
          <a href='/policy' className='user-login-form__link'>Terms of Service</a>
          {' '}and{' '}
          <a href='/policy' className='user-login-form__link'>Privacy Policy</a>
        </p>

        <div className='user-login-form__divider'>
          <span>Or continue with:</span>
        </div>
        <div className='user-login-form__social'>
          <GoogleLogin
            icon={false}
            clientId={process.env.REACT_APP_GOOGLE_APP_ID}
            onSuccess={onGoogleSuccess}
            render={(renderProps) => (
              <button
                type='button'
                className='user-login-form__google-btn'
                onClick={renderProps.onClick}
                disabled={isSubmitting}
              >
                <svg className='user-login-form__google-icon' viewBox='0 0 24 24' width='20' height='20'>
                  <path d='M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z' fill='#4285F4' />
                  <path d='M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z' fill='#34A853' />
                  <path d='M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z' fill='#FBBC05' />
                  <path d='M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z' fill='#EA4335' />
                </svg>
                Google
              </button>
            )}
          />
        </div>

        <div className='user-login-form__footer'>
          Don&apos;t have a co-bee account?{' '}
          <a
            href='/sign-up'
            className='user-login-form__link'
            onClick={(e) => {
              e.preventDefault()
              history.push('/sign-up')
            }}
          >
            Sign up
          </a>
        </div>
      </div>
    </div>
  )
}

UserLoginForm.propTypes = {
  onClose: PropTypes.func,
  onSubmitLogin: PropTypes.func,
  onGoogleSuccess: PropTypes.func,
  isSubmitting: PropTypes.bool,
  initialEmail: PropTypes.string,
}

export default UserLoginForm
